import { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Typography, Paper } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import moment from "moment-timezone";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AxiosInstance from "../../utils/axiosInstance";
import { getItem } from "../../utils/storage";
import { parseDateToUserTimezoneMoment } from "../TimezoneDateFormatter/TimezoneDateFormatter";

const PREVIEW_LIMIT = 2;

const DEFAULT_TZ = "America/New_York";

/**
 * Today vs upcoming for dashboard cards only — by StartDate calendar day in user TZ.
 * Do not use API Status here: cron/business-date can mark Status "Upcoming" while the
 * visit is still on today's calendar in the user's timezone (same issue as MAR 20).
 */
export function categorizeVisitsByUserTimezone(visits, timezone) {
  const tz = timezone || DEFAULT_TZ;
  const todayStart = moment.tz(tz).startOf("day");
  const todayArr = [];
  const upcomingArr = [];

  (visits || []).forEach((appointment) => {
    const m = parseDateToUserTimezoneMoment(appointment?.StartDate, tz, true);
    if (!m.isValid()) return;
    const visitDayStart = m.clone().startOf("day");
    if (visitDayStart.isSame(todayStart, "day")) {
      todayArr.push(appointment);
    } else if (visitDayStart.isAfter(todayStart)) {
      upcomingArr.push(appointment);
    }
  });

  todayArr.sort((a, b) => new Date(a.StartDate) - new Date(b.StartDate));
  upcomingArr.sort((a, b) => new Date(a.StartDate) - new Date(b.StartDate));
  return { today: todayArr, upcoming: upcomingArr };
}

function getVisitDateParts(visit, userTz) {
  const m = parseDateToUserTimezoneMoment(visit?.StartDate, userTz, true);
  if (!m.isValid()) {
    return { month: "—", day: "—", weekday: "" };
  }
  return {
    month: m.format("MMM").toUpperCase(),
    day: m.format("DD"),
    weekday: m.format("ddd"),
  };
}

function visitContractNumber(visit) {
  const n = visit?.ContractData?.ContractNumber;
  if (n === undefined || n === null || n === "") return "";
  return String(n);
}

function visitTitleText(visit) {
  return (visit?.ItemName || visit?.ContractData?.Title || "").trim();
}

function visitCustomerName(visit, tokenDecode) {
  const fromVisit = [visit?.CustomerData?.FirstName, visit?.CustomerData?.LastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fromVisit) return fromVisit;
  const fromToken = [tokenDecode?.FirstName, tokenDecode?.LastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fromToken;
}

function visitAddressFull(visit) {
  return [
    visit?.Location?.Address,
    visit?.Location?.City,
    visit?.Location?.State,
  ]
    .filter(Boolean)
    .join(", ");
}

function FieldLine({ label, value }) {
  const display = value && String(value).trim() ? String(value).trim() : "—";
  return (
    <Typography
      component="div"
      sx={{
        fontSize: { xs: "0.78rem", sm: "0.8rem" },
        lineHeight: 1.45,
        mt: 0.35,
        wordBreak: "break-word",
      }}
    >
      <Box
        component="span"
        sx={{ color: "#6b7280", fontWeight: 600, marginRight: "4px" }}
      >
        {label}
      </Box>
      <Box component="span" sx={{ color: "#1f2937" }}>
        {display}
      </Box>
    </Typography>
  );
}

/**
 * Today + upcoming visit previews for worker / customer dashboards.
 * Render below chart sections (graphs first, then these lists).
 */
export default function DashboardVisitLists({ variant, tokenDecode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const [today, setToday] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  const userTz = useMemo(
    () =>
      tokenDecode?.Timezone && moment.tz.zone(tokenDecode.Timezone)
        ? tokenDecode.Timezone
        : DEFAULT_TZ,
    [tokenDecode?.Timezone]
  );

  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      if (variant === "worker") {
        const companyId = tokenDecode?.CompanyId;
        const workerId = getItem("worker_id");
        if (!companyId || !workerId) {
          setToday([]);
          setUpcoming([]);
          return;
        }
        const res = await AxiosInstance.get(
          `/v1/worker/get-worker-visits/${companyId}/${workerId}`,
          {
            params: {
              pageSize: 1000,
              pageNumber: 0,
              search: "",
              statusFilter: "",
              sortField: "createdAt",
              sortOrder: "desc",
            },
          }
        );
        if (res?.data?.statusCode === 200) {
          const data = res?.data?.data || [];
          const cat = categorizeVisitsByUserTimezone(data, userTz);
          setToday(cat.today);
          setUpcoming(cat.upcoming);
        } else {
          setToday([]);
          setUpcoming([]);
        }
      } else {
        const companyId = tokenDecode?.CompanyId;
        const customerId = getItem("CustomerId");
        if (!companyId || !customerId) {
          setToday([]);
          setUpcoming([]);
          return;
        }
        const res = await AxiosInstance.get(
          `/v1/visit/customer_visits/${companyId}/${customerId}`
        );
        if (res?.data?.statusCode === 200) {
          const data = res?.data?.data;
          if (data?.today && data?.upcoming) {
            const all =
              data?.all ??
              [
                ...(data.today || []),
                ...(data.upcoming || []),
                ...(data.past || []),
              ];
            const cat = categorizeVisitsByUserTimezone(all, userTz);
            setToday(cat.today);
            setUpcoming(cat.upcoming);
          } else if (Array.isArray(data)) {
            const cat = categorizeVisitsByUserTimezone(data, userTz);
            setToday(cat.today);
            setUpcoming(cat.upcoming);
          } else {
            setToday([]);
            setUpcoming([]);
          }
        } else {
          setToday([]);
          setUpcoming([]);
        }
      }
    } catch (e) {
      console.error("DashboardVisitLists:", e);
      setToday([]);
      setUpcoming([]);
    } finally {
      setLoading(false);
    }
  }, [variant, tokenDecode?.CompanyId, userTz]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const baseUrl = CompanyUrl || tokenDecode?.CompanyUrl || "";

  const goToList = (tabIndex) => {
    if (variant === "worker") {
      sessionStorage.setItem("workerAppointmentTab", String(tabIndex));
      navigate(`/${baseUrl}/staff-member/worker-appointments`, {
        state: {
          navigats: ["/index", "/worker-appointments"],
          ...(location?.state || {}),
        },
      });
    } else {
      sessionStorage.setItem("appointmentTab", String(tabIndex));
      navigate(`/${baseUrl}/customers/appointment`, {
        state: {
          navigats: ["/index", "/appointment"],
          ...(location?.state || {}),
        },
      });
    }
  };

  const handleVisitClick = (visit) => {
    if (!visit?.VisitId) return;
    if (variant === "worker") {
      navigate(`/${baseUrl}/staff-member/appointment-confirm`, {
        state: {
          id: visit.VisitId,
          contractId: visit.ContractId,
          navigats: [
            ...(location?.state?.navigats || []),
            "/appointment-confirm",
          ],
        },
      });
    } else {
      navigate(`/${baseUrl}/customers/appointment-confirm`, {
        state: {
          id: visit.VisitId,
          contractId: visit.ContractId,
          navigats: [
            ...(location?.state?.navigats || []),
            "/appointment-confirm",
          ],
        },
      });
    }
  };

  const renderSection = (title, visits, tabIndex, emptyTitle, emptySubtitle) => {
    const slice = (visits || []).slice(0, PREVIEW_LIMIT);
    return (
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
            px: { xs: 0, sm: 0.5 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              color: "#0f2b46",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {title}
          </Typography>
          <Typography
            component="button"
            type="button"
            onClick={() => goToList(tabIndex)}
            sx={{
              border: "none",
              background: "none",
              cursor: "pointer",
              color: "#0a4c9e",
              fontWeight: 600,
              fontSize: "0.9rem",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            View all &gt;
          </Typography>
        </Box>

        {slice.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: "1px solid #e8eaed",
              p: 4,
              textAlign: "center",
              bgcolor: "#fafafa",
            }}
          >
            <EventAvailableOutlinedIcon
              sx={{ fontSize: 48, color: "#bbb", mb: 1 }}
            />
            <Typography sx={{ fontWeight: 600, color: "#666" }}>
              {emptyTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: "#999", mt: 0.5 }}>
              {emptySubtitle}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {slice.map((visit, idx) => {
              const dateParts = getVisitDateParts(visit, userTz);
              return (
              <Paper
                key={visit?.VisitId ?? `visit-${idx}`}
                elevation={0}
                onClick={() => handleVisitClick(visit)}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #e8eaed",
                  overflow: "hidden",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "stretch",
                  "&:hover": { boxShadow: "var(--shadow-md)" },
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    flexShrink: 0,
                    bgcolor: "#0f2b46",
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    flex: 1,
                    py: 1.5,
                    px: 1.5,
                    gap: 1.5,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 1,
                      bgcolor: "#eef1f5",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: "#0f2b46",
                        lineHeight: 1,
                      }}
                    >
                      {dateParts.month}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1.1rem",
                        fontWeight: 700,
                        color: "#0f2b46",
                        lineHeight: 1.2,
                      }}
                    >
                      {dateParts.day}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <CalendarMonthOutlinedIcon
                        sx={{ fontSize: 16, color: "#0f2b46" }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#0f2b46", fontWeight: 500 }}
                      >
                        {dateParts.weekday}
                      </Typography>
                    </Box>
                    <FieldLine
                      label="Contract #:"
                      value={visitContractNumber(visit)}
                    />
                    <FieldLine
                      label="Visit title:"
                      value={visitTitleText(visit)}
                    />
                    <FieldLine
                      label="Customer:"
                      value={visitCustomerName(visit, tokenDecode)}
                    />
                    <FieldLine
                      label="Address:"
                      value={visitAddressFull(visit)}
                    />
                  </Box>
                  <ChevronRightIcon
                    sx={{ color: "#ccc", flexShrink: 0, mt: 0.5 }}
                  />
                </Box>
              </Paper>
            );
            })}
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return null;
  }

  return (
    <Box sx={{ mt: { xs: 2, md: 3 }, width: "100%" }}>
      {renderSection(
        "Today's visits",
        today,
        0,
        "No visits today",
        "Today's visits will show here when scheduled."
      )}
      {renderSection(
        "Upcoming visits",
        upcoming,
        1,
        "No upcoming visits",
        "Your upcoming visits will appear here."
      )}
    </Box>
  );
}
